<template>
  <div class="campaign-result">
    <transition 
      name="fade" 
      mode="out-in"
    >
      <v-card 
        v-if="rewards.length"
        flat 
      >
        <v-card-text>
          <div class="campaign-result-header">
            <h2 class="gray--text">
              Benefícios
            </h2>
            <h3 class="gray--text mt-1 mb-1">
              Disponível para resgate: <strong>{{ getCostValue(balance) }}</strong>
            </h3>
            <span class="gray--text">
              Compre mais com {{ partnerInitials ? `a ${partnerInitials}`: 'o parceiro' }} para liberar mais benefícios.
              <a 
                :href="onLinkPdf" 
                target="blank"
              >
                Regulamento e Produtos Participantes
              </a>
            </span>
          </div>
          <RewardsCard
            :items="rewards"
            :disabled-button="disabledButton || selectedRewards.length === 0"
            :selectedRewards="selectedRewards"
            @rescue-rewards="rescueRewards"
            @select-reward="selectReward"
          />
        </v-card-text>
      </v-card>
    </transition>
    
    <div
      v-if="isNotManualResultMethod"
      class="mt-3"
    >
      <AppBody 
        :table-title="'Meta'" 
        :headers="triggersHeaders" 
        :items="triggers" 
      />
    </div>

    <RewardsRescueModal
      ref="rewardsRescueModal"
      :items="selectedRewards"
      :balance="balance"
      :rescueType="campaign.rebateDetails.rescueType"
      @finalizing-rewards-rescue="finalizingRewardsRescue"
    />
  </div>
</template>

<script>
import AppBody from '@/components/ui/AppBody.vue'

import RewardsCard from './components/RewardsCard.vue'
import RewardsRescueModal from './components/RewardsRescueModal.vue'

import {
  getRewardsRebateCampaigns,
  getTriggersRebateCampaigns
} from '@/services/containers/partners/campaigns.js'

export default {
  name: 'Results',

  components: {
    AppBody,
    RewardsCard,
    RewardsRescueModal
  },

  props: {
    campaign: { type: Object, required: true }
  },

  data: () => ({
    rewards: [],
    triggers: [],
    balance: 0,
    selectedRewards: []
  }),

  created() {
    this.fetch()
  },

  computed: {
    triggersHeaders() {
      return [
        {
          text: 'Família de produtos',
          sortable: false,
          value: 'name',
          vHtml: true,
          width: '300px',
          formatter: ({ name, goal, kpi }) => `${name} <br>Meta: ${goal} ${kpi} em compras`
        },
        {
          text: 'Metas batidas',
          sortable: false,
          value: 'goalAchievedTimes',
          width: '200px',
          formatter: ({ goalAchievedTimes }) => goalAchievedTimes
        },
        {
          text: 'Quantidade comprada ',
          progress: ({ progressBar }) => progressBar,
          sortable: false,
          value: 'progressBar',
          width: '400px',
          formatter: () => ''
        },
        {
          text: 'Volume da meta',
          sortable: false,
          value: 'goal',
          width: '200px',
          formatter: ({ currentGoal, goal }) => `${currentGoal} / ${goal}`
        },
        {
          text: 'Volume acumulado',
          sortable: false,
          value: 'totalVolume',
          width: '200px',
          formatter: ({ totalVolume, kpi }) => `${totalVolume} ${kpi}`
        }
      ]
    },

    disabledButton() {
      return this.rewards.every((reward) => reward.cost > this.balance)
    },

    onLinkPdf() {
      return `https://${process.env.S3_URL}/files/Regulamento_PIP_BRASIL_22-23.pdf`
    },

    partnerInitials() {
      return this.$store.getters.getPartner.initials
    },

    isNotManualResultMethod() {
      return this.campaign?.rebateDetails?.resultMethod !== 'manual'
    }
  },

  methods: {
    async fetch() {
      await this.fetchTriggersRebateCampaigns()
      this.fetchRewardsRebateCampaigns()
    },

    async fetchRewardsRebateCampaigns() {
      this.$showLoading()
      const id = this.$route.params.id
      try {
        const data = await getRewardsRebateCampaigns(id)
     
        this.rewards = data.data.map((product) => ({
          ...product,
          isActive: this.balance >= product.cost,
          img: product.product.images[0],
          title: product.product.label,
          trigger: this.getCostValue(product.cost),
          quantity: 0
        }))
        this.$hideLoading()
      } catch (err) {
        this.$hideLoading()
        this.$toast(err.response.data.message, 'red')
      }
    },

    async fetchTriggersRebateCampaigns() {
      this.$showLoading()
      try {
        const id = this.$route.params.id
        const {
          data: { balance, results }
        } = await getTriggersRebateCampaigns(id)

        this.balance = balance
        this.triggers = results

        this.$hideLoading()
      } catch (err) {
        this.$hideLoading()
        this.$toast(err.response.data.message, 'red')
      }
    },

    getCostValue(value) {
      const isCoin = this.campaign?.rebateDetails?.rescueType === 'coin'
      const toFixed = isCoin ? 0 : 1
      const formatValue = value > 0 ? this.$currencyFormat(value, toFixed) : value

      if (isCoin) {
        return `${formatValue} SDZ`
      } 

      const valueInPlural = `${formatValue}x gatilhos`
      const valueInSingular = `${formatValue}x gatilho`

      const currentValue = value > 1 ? valueInPlural : valueInSingular

      return currentValue
    },

    rescueRewards() {
      this.$refs.rewardsRescueModal.open()
    },

    finalizingRewardsRescue() {
      this.fetchTriggersRebateCampaigns()
    },

    selectReward(item) {
      if (this.selectedRewards.some(elem => elem._id === item._id)) {
        this.selectedRewards = this.selectedRewards.filter(elem => elem._id !== item._id)
        return
      }

      this.selectedRewards = [...this.selectedRewards, item]
    }
  }
}
</script>

<style lang="stylus">
.campaign-result {
  &-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 30px 0;
    flex-direction: column;

    @media (max-width: 768px) {
      text-align: center;
    }

    h3 {
      font-weight: 500;
    }

    a {
      color: #36B19E;
      font-weight: bold;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1.2s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

</style>
